// Graph Builder: read pictographs, bar and line graphs, then build them yourself.

import { h, clear } from '@kit/dom';
import { makeSurface, roundRect } from '@kit/canvas';
import type { GameModule, GameContext } from '@shell/types';
import { makeGraph, type GraphProblem } from '@problems/graphs';

const ITEMS = 6;
const COLORS = ['#e2574c', '#4f8fe6', '#5fbf6a', '#f5c542', '#9b6be6'];

function mount(host: HTMLElement, ctx: GameContext) {
  let n = 0;
  let p!: GraphProblem;
  let level = ctx.currentLevel();
  let maxLevel = level;
  let attempts = 0, correct = 0;
  let misses = 0, hinted = false, locked = false;
  let built: number[] = [];
  const t0 = performance.now();
  let tProblem = performance.now();

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Graph ', h('strong', null, '1'), ` of ${ITEMS}`), h('span.muted.small', null, ''));
  const stage = h('div');
  const question = h('p.center', { style: { fontWeight: '800', fontSize: '18px', minHeight: '44px', padding: '0 4px' } });
  const table = h('div');
  const controls = h('div.stack', { style: { gap: '8px' } });
  host.append(head, stage, question, table, controls);

  const S = makeSurface(stage, { aspect: 0.72, draw: false, onResize: () => draw() });

  function newItem() {
    level = ctx.currentLevel();
    maxLevel = Math.max(maxLevel, level);
    p = makeGraph(level, ctx.rng, n);
    misses = 0; hinted = false; locked = false;
    built = p.startValues ? p.startValues.slice() : [];
    tProblem = performance.now();
    (head.querySelector('strong') as HTMLElement).textContent = String(n + 1);
    (head.lastElementChild as HTMLElement).textContent = p.kind.startsWith('build') ? 'Build it' : 'Read it';
    question.textContent = p.question;
    renderTable();
    renderControls();
    draw();
  }

  function renderTable() {
    clear(table);
    if (!p.kind.startsWith('build')) return;
    table.append(h('div.card', { style: { padding: '8px 12px' } },
      h('p.small.muted', null, p.set.title),
      h('div', { style: { display: 'grid', gridTemplateColumns: `repeat(${p.set.categories.length}, 1fr)`, gap: '4px', textAlign: 'center', fontWeight: '800' } },
        ...p.set.categories.map(c => h('div.small', null, c)),
        ...p.values.map(v => h('div', null, String(v))),
      ),
    ));
  }

  function renderControls() {
    clear(controls);
    if (p.kind.startsWith('build')) {
      const step = p.kind === 'build-picto' ? p.key : p.scale;
      const row = h('div', { style: { display: 'grid', gridTemplateColumns: `repeat(${p.set.categories.length}, 1fr)`, gap: '6px' } });
      p.set.categories.forEach((_, i) => {
        row.append(h('div.stack', { style: { gap: '4px' } },
          h('button.btn.small', { onclick: () => { built[i] += step; draw(); } }, '+'),
          h('button.btn.small', { onclick: () => { built[i] = Math.max(0, built[i] - step); draw(); } }, '−'),
        ));
      });
      controls.append(row, h('div.grid2', null, h('button.btn.ghost', { onclick: hint }, 'Hint'), h('button.btn.primary', { onclick: checkBuild }, 'Check')));
      return;
    }
    controls.append(h('div.choices', null, ...p.choices.map(c => h('button.choice', { style: typeof c === 'string' && c.length > 6 ? { fontSize: '16px' } : {}, onclick: (e: Event) => answer(c, e.currentTarget as HTMLElement) }, String(c)))));
    controls.append(h('button.btn.small.ghost.block', { onclick: hint }, 'Hint'));
  }

  function hint() { hinted = true; ctx.say(`Hint: ${p.hint}`); }

  function checkBuild() {
    if (locked) return;
    const ms = performance.now() - tProblem;
    const ok = built.every((v, i) => v === p.values[i]);
    if (ok) {
      locked = true; attempts++; correct++;
      ctx.report({ skill: 'data', level, correct: true, hintUsed: hinted, ms, problemId: p.id });
      ctx.sound('ding'); ctx.say('That matches the table. Graph built!');
      next();
    } else {
      misses++;
      if (misses === 1) { attempts++; ctx.report({ skill: 'data', level, correct: false, hintUsed: hinted, ms, problemId: p.id }); }
      ctx.sound('buzz');
      const wrong = built.findIndex((v, i) => v !== p.values[i]);
      if (misses >= 2) {
        locked = true; built = p.values.slice(); draw();
        ctx.say(`Here is the finished graph. ${p.set.categories[wrong]} needed ${p.values[wrong]}.`);
        next(2000);
      } else {
        hinted = true;
        ctx.say(`${p.set.categories[wrong]} is not right yet. It should show ${p.values[wrong]}.`);
      }
    }
  }

  function answer(c: number | string, btn: HTMLElement) {
    if (locked) return;
    const ms = performance.now() - tProblem;
    if (c === p.answer) {
      locked = true; attempts++; correct++;
      ctx.report({ skill: 'data', level, correct: true, hintUsed: hinted, ms, problemId: p.id });
      ctx.sound('ding'); btn.classList.add('good');
      ctx.say(`Yes, ${p.answer}.`);
      next();
    } else {
      misses++;
      if (misses === 1) { attempts++; ctx.report({ skill: 'data', level, correct: false, hintUsed: hinted, ms, problemId: p.id }); }
      ctx.sound('buzz'); btn.classList.add('bad'); setTimeout(() => btn.classList.remove('bad'), 500);
      if (misses >= 2) {
        locked = true;
        ctx.say(`It was ${p.answer}. ${p.hint}`);
        for (const b of Array.from(controls.querySelectorAll('.choice')) as HTMLElement[]) if (b.textContent === String(p.answer)) b.classList.add('good');
        next(1800);
      } else { hinted = true; ctx.say(`Not ${c}. ${p.hint}`); }
    }
  }

  function next(delay = 1000) {
    n++;
    if (n >= ITEMS) {
      if (attempts === correct && maxLevel >= 5) ctx.charm('charm.graph');
      setTimeout(() => ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0 }), delay);
      return;
    }
    setTimeout(newItem, delay);
  }

  // ---------- drawing ----------
  function draw() {
    if (!p || !S.w) return;
    const c = S.ctx, W = S.w, H = S.h;
    c.fillStyle = '#fff'; c.fillRect(0, 0, W, H);
    c.fillStyle = '#2a2a2e'; c.font = '800 14px Nunito, sans-serif'; c.textAlign = 'left'; c.textBaseline = 'top';
    c.fillText(p.set.title, 10, 6);
    switch (p.kind) {
      case 'read-picto': drawPicto(c, W, H, p.values, p.key); break;
      case 'build-picto': drawPicto(c, W, H, built, p.key); break;
      case 'build-bar': drawBars(c, 10, 30, W - 20, H - 40, built, p.scale, p.set.categories, Math.max(...p.values) + p.scale); break;
      case 'read-bar': case 'mode': case 'more-than': case 'mean': drawBars(c, 10, 30, W - 20, H - 40, p.values, p.scale, p.set.categories, Math.max(...p.values) + p.scale); break;
      case 'line-read': case 'line-jump': drawLine(c, W, H); break;
      case 'two-series': drawTwo(c, W, H); break;
      case 'truth': drawTruth(c, W, H); break;
    }
  }

  function icon(c: CanvasRenderingContext2D, kind: string, x: number, y: number, s: number) {
    switch (kind) {
      case 'cupcake': c.fillStyle = '#ee86b7'; c.beginPath(); c.arc(x, y - s * 0.15, s * 0.45, Math.PI, 0); c.fill(); c.fillStyle = '#b48a5a'; c.beginPath(); c.moveTo(x - s * 0.4, y - s * 0.1); c.lineTo(x + s * 0.4, y - s * 0.1); c.lineTo(x + s * 0.28, y + s * 0.45); c.lineTo(x - s * 0.28, y + s * 0.45); c.closePath(); c.fill(); break;
      case 'rock': c.fillStyle = '#8c8a86'; c.beginPath(); c.moveTo(x - s * 0.45, y + s * 0.3); c.lineTo(x - s * 0.3, y - s * 0.3); c.lineTo(x + s * 0.2, y - s * 0.45); c.lineTo(x + s * 0.45, y); c.lineTo(x + s * 0.3, y + s * 0.4); c.closePath(); c.fill(); break;
      case 'book': c.fillStyle = '#4f8fe6'; roundRect(c, x - s * 0.35, y - s * 0.45, s * 0.7, s * 0.9, 2); c.fill(); c.fillStyle = '#f6f3ec'; c.fillRect(x - s * 0.25, y - s * 0.2, s * 0.5, 2); break;
      case 'letter': c.fillStyle = '#f6f3ec'; c.fillRect(x - s * 0.45, y - s * 0.3, s * 0.9, s * 0.6); c.strokeStyle = '#2a2a2e'; c.lineWidth = 1.2; c.strokeRect(x - s * 0.45, y - s * 0.3, s * 0.9, s * 0.6); c.beginPath(); c.moveTo(x - s * 0.45, y - s * 0.3); c.lineTo(x, y + s * 0.05); c.lineTo(x + s * 0.45, y - s * 0.3); c.stroke(); break;
      default: c.fillStyle = '#5fbf6a'; c.beginPath(); c.arc(x, y, s * 0.4, 0, 7); c.fill(); c.fillStyle = '#2a2a2e'; c.beginPath(); c.arc(x - s * 0.12, y - s * 0.08, 1.5, 0, 7); c.arc(x + s * 0.12, y - s * 0.08, 1.5, 0, 7); c.fill();
    }
  }

  function drawPicto(c: CanvasRenderingContext2D, W: number, H: number, vals: number[], key: number) {
    const rows = p.set.categories.length;
    const rowH = (H - 60) / rows;
    const labelW = 86;
    const s = Math.min(rowH * 0.7, 26);
    c.font = '700 13px Nunito, sans-serif'; c.textAlign = 'right'; c.textBaseline = 'middle'; c.fillStyle = '#2a2a2e';
    p.set.categories.forEach((cat, i) => {
      const y = 36 + i * rowH + rowH / 2;
      c.fillStyle = '#2a2a2e'; c.fillText(cat, labelW - 8, y);
      c.strokeStyle = '#e2dccb'; c.beginPath(); c.moveTo(labelW, y + rowH / 2); c.lineTo(W - 10, y + rowH / 2); c.stroke();
      const count = Math.round(vals[i] / key);
      for (let k = 0; k < count; k++) icon(c, p.set.icon, labelW + 10 + k * (s + 4) + s / 2, y, s);
    });
    c.font = '700 12px Nunito, sans-serif'; c.textAlign = 'left'; c.fillStyle = '#6a6a72';
    icon(c, p.set.icon, W - 120, H - 12, 16); c.fillText(`= ${key} ${p.set.unit}`, W - 106, H - 12);
  }

  function drawBars(c: CanvasRenderingContext2D, x: number, y: number, w: number, hgt: number, vals: number[], scale: number, cats: string[], max: number, axisStart = 0, color?: string) {
    const left = x + 34, bottom = y + hgt - 22, top = y + 6;
    const span = Math.max(scale, max - axisStart);
    c.strokeStyle = '#2a2a2e'; c.lineWidth = 1.5;
    c.beginPath(); c.moveTo(left, top); c.lineTo(left, bottom); c.lineTo(x + w, bottom); c.stroke();
    c.font = '700 11px Nunito, sans-serif'; c.fillStyle = '#6a6a72'; c.textAlign = 'right'; c.textBaseline = 'middle';
    const ticks = Math.min(12, Math.round(span / scale));
    const tickStep = span / ticks;
    for (let i = 0; i <= ticks; i++) {
      const v = axisStart + i * tickStep;
      const yy = bottom - (bottom - top) * (i / ticks);
      c.strokeStyle = '#e2dccb'; c.beginPath(); c.moveTo(left, yy); c.lineTo(x + w, yy); c.stroke();
      c.fillText(String(Math.round(v)), left - 4, yy);
    }
    const n = vals.length, slot = (x + w - left) / n, bw = Math.min(slot * 0.6, 56);
    vals.forEach((v, i) => {
      const bx = left + i * slot + (slot - bw) / 2;
      const bh = Math.max(0, (bottom - top) * ((v - axisStart) / span));
      c.fillStyle = color || COLORS[i % COLORS.length];
      c.fillRect(bx, bottom - bh, bw, bh);
      c.fillStyle = '#2a2a2e'; c.font = '700 11px Nunito, sans-serif'; c.textAlign = 'center'; c.textBaseline = 'top';
      c.fillText(cats[i], bx + bw / 2, bottom + 4);
      if (p.kind.startsWith('build')) { c.textBaseline = 'bottom'; c.fillText(String(v), bx + bw / 2, bottom - bh - 2); }
    });
  }

  function drawLine(c: CanvasRenderingContext2D, W: number, H: number) {
    const left = 40, bottom = H - 28, top = 32, right = W - 14;
    const max = Math.max(...p.values) + p.scale;
    c.strokeStyle = '#2a2a2e'; c.lineWidth = 1.5; c.beginPath(); c.moveTo(left, top); c.lineTo(left, bottom); c.lineTo(right, bottom); c.stroke();
    c.font = '700 11px Nunito, sans-serif'; c.fillStyle = '#6a6a72'; c.textAlign = 'right'; c.textBaseline = 'middle';
    for (let v = 0; v <= max; v += p.scale) { const yy = bottom - (bottom - top) * (v / max); c.strokeStyle = '#e2dccb'; c.beginPath(); c.moveTo(left, yy); c.lineTo(right, yy); c.stroke(); c.fillText(String(v), left - 4, yy); }
    const n = p.values.length, step = (right - left) / (n - 1 + 0.6);
    const pts = p.values.map((v, i) => [left + step * (i + 0.3), bottom - (bottom - top) * (v / max)] as [number, number]);
    c.strokeStyle = '#4f8fe6'; c.lineWidth = 3; c.lineJoin = 'round'; c.beginPath(); pts.forEach(([px, py], i) => i ? c.lineTo(px, py) : c.moveTo(px, py)); c.stroke();
    pts.forEach(([px, py], i) => { c.fillStyle = '#4f8fe6'; c.beginPath(); c.arc(px, py, 5, 0, 7); c.fill(); c.fillStyle = '#2a2a2e'; c.textAlign = 'center'; c.textBaseline = 'top'; c.fillText(p.set.categories[i], px, bottom + 4); });
  }

  function drawTwo(c: CanvasRenderingContext2D, W: number, H: number) {
    const left = 40, bottom = H - 28, top = 32, right = W - 14;
    const max = Math.max(...p.values, ...p.values2!) + p.scale;
    c.strokeStyle = '#2a2a2e'; c.lineWidth = 1.5; c.beginPath(); c.moveTo(left, top); c.lineTo(left, bottom); c.lineTo(right, bottom); c.stroke();
    c.font = '700 11px Nunito, sans-serif'; c.fillStyle = '#6a6a72'; c.textAlign = 'right'; c.textBaseline = 'middle';
    for (let v = 0; v <= max; v += p.scale) { const yy = bottom - (bottom - top) * (v / max); c.strokeStyle = '#e2dccb'; c.beginPath(); c.moveTo(left, yy); c.lineTo(right, yy); c.stroke(); c.fillText(String(v), left - 4, yy); }
    const n = p.values.length, slot = (right - left) / n, bw = Math.min(slot * 0.32, 26);
    p.values.forEach((v, i) => {
      const bx = left + i * slot + slot / 2;
      const h1 = (bottom - top) * (v / max), h2 = (bottom - top) * (p.values2![i] / max);
      c.fillStyle = '#e2574c'; c.fillRect(bx - bw - 2, bottom - h1, bw, h1);
      c.fillStyle = '#4f8fe6'; c.fillRect(bx + 2, bottom - h2, bw, h2);
      c.fillStyle = '#2a2a2e'; c.textAlign = 'center'; c.textBaseline = 'top'; c.fillText(p.set.categories[i], bx, bottom + 4);
    });
    c.textAlign = 'left'; c.textBaseline = 'top';
    c.fillStyle = '#e2574c'; c.fillRect(W - 110, 8, 12, 12); c.fillStyle = '#2a2a2e'; c.fillText(p.series[0], W - 94, 8);
    c.fillStyle = '#4f8fe6'; c.fillRect(W - 58, 8, 12, 12); c.fillStyle = '#2a2a2e'; c.fillText(p.series[1], W - 42, 8);
  }

  function drawTruth(c: CanvasRenderingContext2D, W: number, H: number) {
    const half = (W - 30) / 2;
    const max = Math.max(...p.values) + 10;
    c.fillStyle = '#2a2a2e'; c.font = '800 13px Nunito, sans-serif'; c.textAlign = 'center'; c.textBaseline = 'top';
    c.fillText('Graph A', 10 + half / 2, 26); c.fillText('Graph B', 20 + half + half / 2, 26);
    drawBars(c, 10, 40, half, H - 50, p.values, 10, p.set.categories, max, 0, '#5fbf6a');
    drawBars(c, 20 + half, 40, half, H - 50, p.values, 1, p.set.categories, Math.max(...p.values) + 1, p.axisStartB!, '#f08a3c');
  }

  newItem();
  return { destroy() { S.destroy(); clear(host); } };
}

const mod: GameModule = {
  id: 'math.graph-builder',
  title: 'Graph Builder',
  blurb: 'Read pictographs, bars and lines, then build graphs from a table.',
  category: 'math',
  skills: ['data'],
  levelRange: [1, 10],
  unlock: { stars: 12 },
  mount,
};
export default mod;
